import { Button } from '@consta/uikit/Button'
import { Modal } from '@consta/uikit/Modal'
import { Text } from '@consta/uikit/Text'
import { FC, useState, useEffect } from 'react'
import { IconClose } from '@consta/icons/IconClose'
import { Card } from '@consta/uikit/Card'
import { Combobox } from '@consta/uikit/Combobox'
import { 
    useLazyGetWellQuery,
    useLazyGetWellLabelInfoQuery,
    usePostExportLabelMutation,
    useBatchExportLabelMutation 
} from '../../../services/wellService';

type MonitoringStage3Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    handleCloseModal: () => void;
    projectUuid: string;
}

type WellItem = {
    id: number;
    label: string;
    uuid: string;
}

type LabelInfo = {
    well_number: number;
    company_name: string;
    team: string;
    workshop: string;
    volume: number;
    field: string;
}

const MonitoringStage3: FC<MonitoringStage3Props> = ({ 
    isOpen, 
    setIsOpen, 
    handleCloseModal,
    projectUuid 
}) => {
    // Состояния
    const [wells, setWells] = useState<WellItem[]>([]);
    const [selectedWell, setSelectedWell] = useState<WellItem | null>(null);
    const [labelInfo, setLabelInfo] = useState<LabelInfo | null>(null);
    const [isLoadingWells, setIsLoadingWells] = useState(false);
    const [isLoadingLabelInfo, setIsLoadingLabelInfo] = useState(false);
    const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);

    // API хуки
    const [getWells] = useLazyGetWellQuery();
    const [getWellLabelInfo] = useLazyGetWellLabelInfoQuery();
    const [postExportLabel, { isLoading: isExportingSingle }] = usePostExportLabelMutation();
    const [batchExportLabel, { isLoading: isExportingBatch }] = useBatchExportLabelMutation();

    // Функция для скачивания файла
    const downloadFile = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Показ уведомления
    const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    // Загрузка списка скважин при открытии модального окна
    useEffect(() => {
        if (isOpen && projectUuid) {
            loadWells();
        }
    }, [isOpen, projectUuid]);

    // Загрузка информации о выбранной скважине
    useEffect(() => {
        if (selectedWell?.uuid) {
            loadLabelInfo(selectedWell.uuid);
        } else {
            setLabelInfo(null);
        }
    }, [selectedWell]);

    // Функция загрузки списка скважин
    const loadWells = async () => {
        setIsLoadingWells(true);
        try {
            const response = await getWells({project: projectUuid, limit: 9999, ordering: 'number'}).unwrap();
            
            if (response.results && Array.isArray(response.results)) {
                const wellItems: WellItem[] = response.results.map((well: any) => ({
                    id: well.id,
                    label: `Скв. ${well.number}`,
                    uuid: well.uuid
                }));
                setWells(wellItems);
                
                if (wellItems.length > 0 && !selectedWell) {
                    setSelectedWell(wellItems[0]);
                }
            }
        } catch (error) {
            console.error('❌ Ошибка при загрузке скважин:', error);
            showNotification('Ошибка при загрузке списка скважин', 'error');
        } finally {
            setIsLoadingWells(false);
        }
    };

    // Функция загрузки информации для этикетки
    const loadLabelInfo = async (wellUuid: string) => {
        setIsLoadingLabelInfo(true);
        try {
            const response = await getWellLabelInfo({ well_uuid: wellUuid }).unwrap();
            setLabelInfo(response);
        } catch (error) {
            console.error('❌ Ошибка при загрузке информации для этикетки:', error);
            setLabelInfo(null);
            showNotification('Ошибка при загрузке информации для этикетки', 'error');
        } finally {
            setIsLoadingLabelInfo(false);
        }
    };

    // Обработчик печати текущей этикетки
    const handlePrintCurrent = async () => {
        if (!selectedWell?.uuid) {
            showNotification('Выберите скважину для печати', 'error');
            return;
        }

        try {
            // Используем unwrap() который теперь правильно обрабатывает blob
            const blob: any = await postExportLabel({ well_uuid: selectedWell.uuid }).unwrap();
            const filename = `этикетка_скважина_${selectedWell.label.replace('Скв. ', '')}.pdf`;
            downloadFile(blob, filename);
            showNotification('Этикетка успешно сформирована и скачана');
        } catch (error: any) {
            console.error('❌ Ошибка при печати текущей этикетки:', error);
            showNotification('Ошибка при формировании этикетки для печати', 'error');
        }
    };

    // Обработчик печати всех этикеток
    const handlePrintAll = async () => {
        if (!projectUuid) {
            showNotification('Не указан проект', 'error');
            return;
        }

        try {
            // Используем unwrap() который теперь правильно обрабатывает blob
            const blob: any = await batchExportLabel({ project_uuid: projectUuid }).unwrap();
            const filename = `этикетки_проект_${projectUuid}.pdf`;
            downloadFile(blob, filename);
            showNotification('Все этикетки успешно сформированы и скачаны');
        } catch (error: any) {
            console.error('❌ Ошибка при печати всех этикеток:', error);
            showNotification('Ошибка при формировании всех этикеток', 'error');
        }
    };

    // Обработчик закрытия модального окна
    const handleClose = () => {
        setSelectedWell(null);
        setLabelInfo(null);
        setNotification(null);
        handleCloseModal();
    };

    return (
        <Modal
            isOpen={isOpen}
            hasOverlay
            onClickOutside={handleClose}
            onEsc={handleClose}
            style={{
                width: '600px',
                maxWidth: '90vw'
            }}
        >
            <div style={{ 
                padding: '20px', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '20px' 
            }}>
                {/* Заголовок */}
                <div style={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <Text size='l' weight='bold'>Этикетка для тары</Text>
                    <Button 
                        size='s' 
                        iconLeft={IconClose} 
                        view='clear' 
                        onClick={handleClose}
                    />
                </div>

                {/* Уведомление */}
                {notification && (
                    <Card style={{
                        padding: '12px 16px',
                        backgroundColor: notification.type === 'success' ? '#f6ffed' : '#fff2f0',
                        border: `1px solid ${notification.type === 'success' ? '#b7eb8f' : '#ffccc7'}`
                    }}>
                        <Text 
                            size="s" 
                            style={{ 
                                color: notification.type === 'success' ? '#52c41a' : '#ff4d4f' 
                            }}
                        >
                            {notification.message}
                        </Text>
                    </Card>
                )}
                
                {/* Выбор скважины */}
                <div>
                    <Text size='s' view='secondary' style={{ marginBottom: '8px' }}>
                        Выберите скважину:
                    </Text>
                    <Combobox
                        items={wells}
                        value={selectedWell}
                        onChange={setSelectedWell}
                        placeholder={
                            isLoadingWells ? "Загрузка скважин..." : "Выберите скважину"
                        }
                        disabled={isLoadingWells || wells.length === 0}
                    />
                    {isLoadingWells && (
                        <Text size='s' view='secondary' style={{ marginTop: '4px' }}>
                            Загрузка списка скважин...
                        </Text>
                    )}
                </div>

                {/* Информация для этикетки */}
                <Card style={{
                    padding: '20px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px',
                    minHeight: '200px'
                }}>
                    {isLoadingLabelInfo ? (
                        <Text>Загрузка информации...</Text>
                    ) : labelInfo ? (
                        <>
                            <Text weight='bold'>{'ООО "Геоиндикатор"'}</Text>
                            <Text>Заказчик: {labelInfo.company_name || 'ООО "ГеоСкан"'}</Text>
                            <Text>Месторождение: {labelInfo.field || 'Северное'}</Text>
                            <Text>№ скважины: {labelInfo.well_number || selectedWell?.label.replace('Скв. ', '')}</Text>
                            <Text>Цех: {labelInfo.workshop || '2'}</Text>
                            <Text>Бригада: {labelInfo.team || '3'}</Text>
                            <Text>Дата: ______________________________</Text>
                            <Text>Объем пробы: {labelInfo.volume ? `${labelInfo.volume} л` : '____л'}</Text>
                        </>
                    ) : selectedWell ? (
                        <Text view='secondary'>
                            Информация по выбранной скважине не найдена
                        </Text>
                    ) : (
                        <Text view='secondary'>
                            Выберите скважину для отображения информации
                        </Text>
                    )}
                </Card>

                {/* Кнопки действий */}
                <div style={{
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '20px'
                }}>
                    <Button
                        size="m"
                        view="ghost"
                        label="Назад"
                        width="default"
                        onClick={handleClose}
                        disabled={isExportingSingle || isExportingBatch}
                    />
                    <Button
                        size="m"
                        view="primary"
                        label={
                            isExportingSingle ? "Формирование..." : "Скачать текущую"
                        }
                        width="default"
                        onClick={handlePrintCurrent}
                        loading={isExportingSingle}
                        disabled={!selectedWell || isExportingSingle || isExportingBatch}
                    />
                    <Button
                        size="m"
                        view="primary"
                        label={
                            isExportingBatch ? "Формирование..." : "Скачать все"
                        }
                        width="default"
                        style={{ backgroundColor: 'green' }}
                        onClick={handlePrintAll}
                        loading={isExportingBatch}
                        disabled={!projectUuid || isExportingSingle || isExportingBatch}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default MonitoringStage3;